<template>
  <v-card class="step-tab-panel" flat>
    <BaseProgressBar v-if="isLoading" :message="t('general.loading')" />
    <v-container
      v-else
      class="pa-0 ma-0 d-flex flex-column flex-grow-1 date-assortment-groups__container"
    >
      <v-row class="date-assortment-groups__header flex-grow-0 ma-0">
        <v-col>
          <!-- Dates and assortment groups title -->
          <span class="font-weight-bold mr-1">{{ tkey('infoNote') }}</span>
        </v-col>
      </v-row>

      <v-row class="date-assortment-groups__form flex-grow-1">
        <v-col class="pr-4" cols="4">
          <!-- Previous template linking section -->
          <template v-if="previousWorkpackageLinkingEnabled && !isLoading">
            <v-row class="mb-4">
              <!-- Previous template column -->
              <v-col cols="6">
                <h3 class="mb-2">
                  {{ tkey('previousTemplateLabel') }}

                  <!-- Previous template tooltip -->
                  <AssortmentTooltip
                    :title="$t('tooltips.previousTemplate.title')"
                    :tooltip-sections="previousTemplateTooltipSections"
                  />
                </h3>
                <AssortmentSearch
                  id="templatesSearch"
                  item-text="name"
                  :item-label="tp => tp.name"
                  :disabled="isWorkpackageSetupRunning"
                  :on-search="onPreviousTemplateSearch"
                  async-search
                  search-on-demand
                  :results-limit="100"
                  :placeholder="tkey('selectPreviousTemplatePlaceholder')"
                  :on-selection="handlePreviousTemplateChange"
                  :initial-item="initialPreviousTemplate"
                />
              </v-col>
            </v-row>
          </template>
          <!-- Dates Selection Section -->
          <DatesSelection />
        </v-col>

        <v-col class="pl-3 pr-3 col-7 col-border-left">
          <AssortmentGroupsSelection
            v-if="wpDateSelections.snapshotDate"
            ref="assortmentGroupSelection"
            :snapshot-date="wpDateSelections.snapshotDate"
            :reset-assortment-groups="resetAssortmentGroups"
            :selected-assortment-group-settings="wpSelectedAssortmentGroups"
          />
        </v-col>
      </v-row>

      <v-row class="date-assortment-groups__footer flex-grow-0">
        <v-col class="d-flex">
          <PageActions
            :has-data-changes="hasDataChanges"
            :has-data-errors="hasDataErrors"
            @discard="discardChanges"
            @save="saveTemplate(false)"
          >
            <template #left-btns>
              <div class="d-flex align-center ga-2">
                <v-divider class="mr-3 divider" vertical />
                <ImportFromPlanogram
                  class="import-from-planogram"
                  :should-be-disabled="hasDataChanges || isLoading"
                />

                <v-divider class="mr-3 divider" vertical />

                <v-tooltip
                  :model-value="hasInvalidPeriodIds"
                  :disabled="!hasInvalidPeriodIds"
                  location="top"
                >
                  <template #activator="{ props }">
                    <div v-bind="props">
                      <v-btn
                        data-id-e2e="btnRunTemplate"
                        class="mx-0"
                        :disabled="isEditingDisabled || hasDataChanges || isWorkflowLoading"
                        :loading="isWorkpackageSetupRunning || isWorkflowLoading"
                        variant="flat"
                        primary
                        min-width="65"
                        @click="runTemplateSetup(false)"
                      >
                        {{ $t('datesAssortmentGroupsPage.runWp') }}
                      </v-btn>
                    </div>
                  </template>
                  <span v-if="hasInvalidPeriodIds">{{
                    $t('workpackagePage.scope.warnings.periodIdWarning')
                  }}</span>
                </v-tooltip>
              </div>
            </template>
          </PageActions>

          <DependencyTreeFeedbackModal
            class="dependency-modal"
            :value="dependencyTreeModalOpen"
            :results="dependencyTreeFeedback"
            page="workpackageSetup"
            @close="closeDependencyTreeModal"
            @commit="commitHandler"
          />
          <UnsavedDataModal
            ref="unsavedDataModal"
            :value="isUnsavedDataModalOpen"
            @cancel="closeUnsavedDataModal"
            @confirm="confirmUnsavedDataModal"
          />
        </v-col>
      </v-row>
    </v-container>
  </v-card>
</template>

<script>
import to from 'await-to-js';
import { get, isEqual, keys, omit, pick, pickBy, sortBy } from 'lodash';
import { mapActions, mapState } from 'pinia';

// Composables
import { useInputValidations } from '@/composables/input-validations';
import { useUnsavedDataWarning } from '@/composables/unsaved-data-warning';
import { useInternationalization } from '@/composables/use-internationalization';
import { usePermission } from '@/composables/use-permission';
import { useAsyncGuard } from '@/composables/use-async-guard';

// Stores
import { useContextStore } from '@/stores/context';
import { useDependencyTreeStore } from '@/stores/dependency-tree';
import { useScenariosStore } from '@/stores/scenarios';
import { useSnackbarStore } from '@/stores/snackbar';
import { useWorkpackagesStore } from '@/stores/workpackages';
import { useFeature } from '@/composables/use-feature';

export default {
  beforeRouteLeave(routeTo, from, next) {
    this.beforeRouteLeave(routeTo, from, next);
  },

  setup() {
    const { canEditWorkpackageScope } = usePermission();
    const {
      isUnsavedDataModalOpen,
      closeUnsavedDataModal,
      confirmUnsavedDataModal,
      beforeRouteLeave,
    } = useUnsavedDataWarning();

    const {
      required,
      isInteger,
      isNumber,
      isLessThan,
      isAlphanumeric,
      isPositive,
      isNotEmpty,
      isGreaterThan,
      isGreaterOrEqual,
      isNameUnique,
    } = useInputValidations();

    const { t, tkey } = useInternationalization('datesAssortmentGroupsPage');
    const {
      calculateTrendEnabled,
      hasPeriodIdsEnabled,
      betaHasMandatoryPeriodIdsEnabled,
      previousWorkpackageLinkingEnabled,
      defaultFillAccordingTo,
    } = useFeature();

    const { isLoading: isWorkflowLoading, runWithLock } = useAsyncGuard();

    return {
      canEditWorkpackageScope,
      isUnsavedDataModalOpen,
      closeUnsavedDataModal,
      confirmUnsavedDataModal,
      beforeRouteLeave,
      runWithLock,
      required,
      isInteger,
      isNumber,
      isLessThan,
      isAlphanumeric,
      isPositive,
      isNotEmpty,
      isGreaterThan,
      isGreaterOrEqual,
      isNameUnique,
      isWorkflowLoading,
      t,
      tkey,

      calculateTrendEnabled,
      hasPeriodIdsEnabled,
      betaHasMandatoryPeriodIdsEnabled,
      previousWorkpackageLinkingEnabled,
      defaultFillAccordingTo,
    };
  },

  data() {
    return {
      isLoading: false,
      resetAssortmentGroups: true,
      dependencyTreeModalOpen: false,
      dependencyTreeFeedback: {},
      initialPreviousTemplate: null, // a reference to render WP search component on init

      // This is a flag for dependency tree commit handler to understand what to invoke.
      // Set to true when "Run Template Setup" is clicked. False when "Save" is clicked.
      isCalledFromSetup: false,
    };
  },

  computed: {
    ...mapState(useWorkpackagesStore, [
      'selectedWorkpackage',
      'wpValidation',
      'wpDateSelections',
      'wpModelSelections',
      'wpSelectedAssortmentGroups',
      'isWorkpackageSetupRunning',
      'loadingArchivedTemplates',
    ]),
    ...mapState(useContextStore, ['clientConfig', 'getDefaultReverseFormat']),
    ...mapState(useScenariosStore, ['selectedScenario']),

    workpackage() {
      return this.selectedWorkpackage || {};
    },

    hasDataChanges() {
      // These are saved in arbitrary order, sort for comparison.
      const selectedAssortmentGroupSettings = sortBy(
        this.selectedWorkpackage.selectedAssortmentGroupSettings,
        'key'
      );
      const savedWP = { ...this.selectedWorkpackage, selectedAssortmentGroupSettings };

      const wpToSave = this.getWpDataForSave();
      const savedFields = keys(savedWP);
      const updates = pick(wpToSave, savedFields);

      // Some fields are explicitly excluded from the comparison because they relate to internal workpackage state, rather than fields a user can edit.
      const keysToOmit = ['jobs'];
      return !isEqual(omit(savedWP, keysToOmit), omit(updates, keysToOmit));
    },

    hasDataErrors() {
      return this.calculateTrendEnabled && this.validateMaximumTrend();
    },

    isEditingDisabled() {
      return !this.canEditWorkpackageScope;
    },

    defaultMaximumTrend() {
      return get(this.clientConfig, 'maximumTrend');
    },

    hasInvalidPeriodIds() {
      return (
        (!this.wpModelSelections.periodIds || this.wpModelSelections.periodIds.length === 0) &&
        this.hasPeriodIdsEnabled &&
        this.betaHasMandatoryPeriodIdsEnabled
      );
    },
  },

  async created() {
    this.isLoading = true;
    try {
      await this.initialiseWorkpackageData();
    } catch (e) {
      console.error(`Failed to initialise template dates settings: ${e}`);
    } finally {
      this.isLoading = false;
    }
  },

  methods: {
    ...mapActions(useScenariosStore, ['setSelectedScenario']),
    ...mapActions(useDependencyTreeStore, ['triggerDependencyTree']),
    ...mapActions(useSnackbarStore, ['showError']),
    ...mapActions(useWorkpackagesStore, [
      'updateWpModelSelections',
      'getDatesForSave',
      'updateWorkpackage',
      'refreshWorkpackageInformation',
      'getWpDataForSave',
      'resetWpSetupChanges',
      'fetchWorkpackages',
      'fetchSingleWorkpackage',
      'runWorkpackageSetupJob',
    ]),

    updateModel(val, field) {
      this.updateWpModelSelections({ [field]: val });
    },

    async initialiseWorkpackageData() {
      const updates = {
        _id: this.workpackage._id,
        periodIds: this.workpackage.periodIds,
        selectedChain: this.workpackage.selectedChain,
        fillInSelection: this.workpackage.fillInSelection || this.defaultFillAccordingTo,
        useMarkdownAndWasteToCalculateMarginAndPrice:
          this.workpackage.useMarkdownAndWasteToCalculateMarginAndPrice || false,
        linkedPreviousWorkpackageId: this.workpackage.linkedPreviousWorkpackageId,
        linkedPreviousScenarioId: this.workpackage.linkedPreviousScenarioId,
        ...(this.calculateTrendEnabled && {
          maximumTrend: this.workpackage.maximumTrend || this.defaultMaximumTrend,
        }),
      };

      this.updateWpModelSelections(updates);

      // if there is a linked previous template, fetch it for the first time
      if (this.previousWorkpackageLinkingEnabled) {
        await this.setLinkedPreviousTemplate();
      }
    },

    // A handler to fetch matching possible previous templates by name
    onPreviousTemplateSearch(query) {
      return this.fetchWorkpackages({
        regexSearch: query,
        shouldGetTemplates: true,
        ignoreArchivedFlag: true,
        shouldUpdateState: false,
        shouldShowSpinner: false,
      });
    },

    // Update the model with the selected previous template.
    async handlePreviousTemplateChange(newVal) {
      if (!newVal) {
        // if no template is selected, reset the template selection
        this.updateModel(null, 'linkedPreviousWorkpackageId');
        return;
      }

      // update the state
      this.updateModel(newVal._id, 'linkedPreviousWorkpackageId');
    },

    async setLinkedPreviousTemplate() {
      // feetch and set the linked previous template
      if (this.wpModelSelections.linkedPreviousWorkpackageId) {
        this.initialPreviousTemplate = await this.fetchSingleWorkpackage({
          workpackageId: this.wpModelSelections.linkedPreviousWorkpackageId,
          isStatelessFetch: true,
        });
        if (this.initialPreviousTemplate) {
          await this.handlePreviousTemplateChange(this.initialPreviousTemplate);
        }
      }
    },

    async saveTemplate(commit = false) {
      const workpackage = this.getWpDataForSave();
      // The data we receive here will contail the entire object being modified, instead of the data diff.
      // So we pick the difference to validate on the dependency tree changes.
      const changedData = pickBy(workpackage, (val, field) => {
        const savedVal = this.selectedWorkpackage[field];
        return !isEqual(val, savedVal);
      });

      const results = await this.triggerDependencyTree({
        params: {
          change: 'workpackageSetupModified',
          updates: { global: changedData },
          commit,
          workpackageId: this.workpackage._id,
        },
      });

      if (results.needsFeedback) {
        this.dependencyTreeFeedback = results.output;
        this.dependencyTreeModalOpen = true;
        return false; // Stop the save process
      }

      // If the dependency tree is triggered, the template scenario will be removed
      // Clear the deleted scenario from state to ensure that toolbar tabs dependent
      // on the scenario are disabled
      if (commit) this.setSelectedScenario({});

      const workpackageId = this.workpackage._id;
      const [err] = await to(this.updateWorkpackage({ workpackageId, updates: workpackage }));

      if (err) {
        const message = (err && err.message) || this.$t('errors.generalErrorMessage');
        this.showError(message);
        return false;
      }

      // Reload the workpackage after successful save to ensure state is up to date
      await this.refreshWorkpackageInformation({
        workpackageId: this.workpackage._id,
        forceStateUpdate: true,
      });
      return true;
    },

    async commitHandler() {
      if (this.isCalledFromSetup) {
        await this.runTemplateSetup(true);
      } else {
        await this.saveTemplate(true);
        await this.initialiseWorkpackageData();
      }
    },

    async runTemplateSetup(commit = false) {
      this.isCalledFromSetup = true;
      this.runWithLock(async () => {
        // Save the template first, then run the setup if save was successful
        const shouldProceed = await this.saveTemplate(commit);
        if (shouldProceed) {
          await this.runWorkpackageSetupJob();
        }
      });
    },

    discardChanges() {
      this.resetWpSetupChanges();
    },

    validateMaximumTrend() {
      const maximumTrend = this.wpModelSelections.maximumTrend;
      return (
        !maximumTrend ||
        this.isNumber(maximumTrend) !== true ||
        maximumTrend > 100 ||
        this.isGreaterOrEqual(maximumTrend, 0) !== true
      );
    },

    closeDependencyTreeModal() {
      this.dependencyTreeModalOpen = false;
      this.dependencyTreeFeedback = {};
      this.isCalledFromSetup = false;
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@style/base/_variables.scss';

.date-assortment-groups {
  &__container {
    max-width: 100%;
  }

  &__header {
    border-bottom: 1px solid $assortment-divider-colour;
  }

  &__form {
    padding: 10px 5px 25px;
  }

  &__footer {
    background-color: $assortment-workpackage-bg-colour;
    border-top: 1px solid $assortment-divider-colour;

    .actions-row {
      flex: auto;
      flex-direction: row-reverse;
      justify-content: flex-start !important;
    }
  }
}

.col-border-left {
  border-left: 1px solid #ccc;
}

.import-from-planogram {
  margin-top: 2px;
}

:deep(.container.rtls-calendar-picker),
:deep(.date-input.date-input-exclude),
:deep(.assortment-groups) {
  .v-input__slot,
  .v-select__slot {
    background-color: $assortment-control-secondary-bg-colour !important;
    height: 30px !important;
  }
}

:deep(.v-select__selections input) {
  padding-left: 5px;
}

:deep(.v-select__selection) {
  padding-left: 5px !important;

  &.v-select__selection--comma {
    font-weight: bold;
  }
}

:deep(.assortment-groups .list) {
  border: 1px solid $assortment-horizontal-border-colour;
}

:deep(.actions-row) {
  .action-btn-container {
    margin-right: 6px !important;
  }

  .btn-container {
    display: flex;
    padding-left: 0 !important;
  }
}
</style>
